import { Helmet } from 'react-helmet'
import { I18nextProvider } from 'react-i18next'
import { Provider } from 'react-redux'
import { persistStore } from 'redux-persist'
import { PersistGate } from 'redux-persist/integration/react'
import { ThemeProvider, StyleSheetManager } from 'styled-components'
import { App as AntdApp } from 'antd'
import { Config, theme as BriqCommonTheme, Helpers } from '@briq/shared'

import { BriqTheme, GlobalStyle } from './components/theme'
import { ViewportSizeContextProvider } from './components/ViewPortSizeContextProvider'
import { AppConfigContext, DefaultAppConfig } from './config/configContext'
import routes from './i18n/routes'
import { makeStore, ReduxStoreConfig } from './redux/store'
import { routesComponentConfig } from './routes/routeComponents'
import { routesConfig } from './routes/routes'
import { applyAxiosInterceptors } from './utils/axios'
import { stringifyGetParams } from './utils/routes'
import { GlobalEventHandler } from './globalEventHandler'
import { AppRoutes } from './routes/AppRoutes'

export const createBriqProvider = (theme: BriqTheme, config: ReduxStoreConfig) => {
  const { appConfig, i18Next } = config

  i18Next.addResourceBundle('en', 'routes', routes)

  config.axios.defaults.paramsSerializer = stringifyGetParams

  const store = makeStore(config)

  const persistor = persistStore(store)

  applyAxiosInterceptors(config.axios, store)

  const BriqProvider = () => {
    return (
      <PersistGate persistor={persistor}>
        <AppConfigContext.Provider
          value={{
            ...DefaultAppConfig,
            ...appConfig,
            routes: {
              ...routesConfig,
              ...appConfig?.routes
            },
            routesComponentConfig: {
              ...routesComponentConfig,
              ...appConfig?.routesComponentConfig
            }
          }}
        >
          <Config.AppConfigContext.Provider
            value={{
              ...Config.DefaultAppConfig,
              ...appConfig,
              axios: config.axios,
              icons: config.icons,
              images: config.images
            }}
          >
            <I18nextProvider i18n={i18Next}>
              <Provider store={store}>
                <AntdApp>
                  <GlobalEventHandler />
                  <StyleSheetManager shouldForwardProp={Helpers.shouldForwardProp}>
                    <BriqCommonTheme.ThemeContext.Provider value={theme}>
                      <ThemeProvider theme={theme}>
                        <ViewportSizeContextProvider>
                          <Helmet />
                          <GlobalStyle />
                          <AppRoutes />
                        </ViewportSizeContextProvider>
                      </ThemeProvider>
                    </BriqCommonTheme.ThemeContext.Provider>
                  </StyleSheetManager>
                </AntdApp>
              </Provider>
            </I18nextProvider>
          </Config.AppConfigContext.Provider>
        </AppConfigContext.Provider>
      </PersistGate>
    )
  }

  return {
    BriqProvider,
    store
  }
}
